/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { Dialog, DialogPortal, DialogOverlay } from '../ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, Sparkles, Zap, Brain, Wand2 } from 'lucide-react';
import AIAskBox from '../dashboard/AIAskBox';

/**
 * AIChatModal — Point d'entrée unique du chat Pilote IA.
 *
 * Centralise toutes les interactions IA conversationnelles (anciens onglets
 * "Demander à l'IA" dispersés). Accessible via le bouton header de chaque
 * cockpit (Dirigeant + Agent).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - role: 'admin' | 'agent' | 'super_admin'
 */
const DEPTH_OPTIONS = [
    {
        value: 'auto',
        label: 'Auto',
        icon: Wand2,
        hint: "Homly choisit le niveau selon la complexité de la question.",
    },
    {
        value: 'rapide',
        label: 'Rapide',
        icon: Zap,
        hint: "Réponse en quelques secondes — idéal pour les questions courtes et factuelles.",
    },
    {
        value: 'approfondie',
        label: 'Approfondie',
        icon: Brain,
        hint: "Raisonnement Claude Sonnet 4.5 — pour les analyses stratégiques, comparaisons, recommandations détaillées.",
    },
];

export default function AIChatModal({ open, onClose, role = 'admin' }) {
    const [depth, setDepth] = useState(() => {
        try {
            const stored = localStorage.getItem('homly_ai_depth_pref');
            if (stored && ['auto', 'rapide', 'approfondie'].includes(stored)) return stored;
        } catch { /* localStorage indisponible */ }
        return 'auto';
    });

    const handleDepthChange = (v) => {
        setDepth(v);
        try { localStorage.setItem('homly_ai_depth_pref', v); } catch { /* quota */ }
    };

    const title =
        role === 'agent'
            ? 'Coach IA — Préparez vos rendez-vous, vos relances'
            : 'Pilote IA — Demandez tout sur votre agence';

    const activeOption = DEPTH_OPTIONS.find((o) => o.value === depth) || DEPTH_OPTIONS[0];

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogPortal>
                <DialogOverlay className="bg-black/70 backdrop-blur-sm" />
                <DialogPrimitive.Content
                    className="fixed inset-x-2 sm:inset-x-4 top-4 bottom-4 sm:top-8 sm:bottom-8 md:left-[50%] md:right-auto md:translate-x-[-50%] md:w-[min(900px,calc(100vw-4rem))] md:max-w-[900px] z-50 bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 duration-200"
                    data-testid="ai-chat-modal"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-gradient-to-r from-[#681793]/10 to-[#FF914D]/5">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] shrink-0">
                                <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-sm sm:text-base font-bold text-foreground truncate" data-testid="ai-chat-modal-title">
                                    {title}
                                </h2>
                                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">
                                    Historique conservé · Vérifié contre vos données · 👍 / 👎 pour affiner
                                </p>
                            </div>
                        </div>
                        <DialogPrimitive.Close
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
                            data-testid="ai-chat-modal-close"
                            aria-label="Fermer"
                        >
                            <X className="w-4 h-4" />
                        </DialogPrimitive.Close>
                    </div>

                    {/* Depth selector — segmented control */}
                    <div className="px-5 pt-3 pb-2 border-b border-border/60">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/40 border border-border" role="tablist" aria-label="Niveau d'analyse">
                                {DEPTH_OPTIONS.map((opt) => {
                                    const Icon = opt.icon;
                                    const isActive = depth === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive}
                                            onClick={() => handleDepthChange(opt.value)}
                                            data-testid={`ai-depth-${opt.value}`}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                                isActive
                                                    ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-sm'
                                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                                            }`}
                                        >
                                            <Icon className="w-3.5 h-3.5" />
                                            <span>{opt.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-snug max-w-md" data-testid="ai-depth-hint">
                                {activeOption.hint}
                            </p>
                        </div>
                    </div>

                    {/* Content — scrollable AIAskBox */}
                    <div className="flex-1 overflow-y-auto p-4 sm:p-5">
                        <AIAskBox role={role} depth={depth} />
                    </div>
                </DialogPrimitive.Content>
            </DialogPortal>
        </Dialog>
    );
}
